import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Edit,
  Logout,
  Settings,
  History,
  Star,
  StarBorder,
  Language,
  Palette,
  CurrencyExchange,
  Favorite,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import UserPreferencesForm from '../components/user/UserPreferencesForm';

const UserProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [showPreferences, setShowPreferences] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    // TODO: Implement account deletion
    console.log('Delete account functionality to be implemented');
    setShowDeleteConfirm(false);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Profile Header */}
      <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'primary.main',
              fontSize: '2rem',
              mr: 3,
            }}
          >
            {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {user.displayName || user.email}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {user.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('user.memberSince')}: {formatDate(user.createdAt)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('user.lastLogin')}: {formatDate(user.lastLogin)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => setShowPreferences(true)}
            >
              {t('user.preferences')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Logout />}
              onClick={handleLogout}
              disabled={isLoading}
            >
              {t('user.logout')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Quick Stats */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <History sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {user.preferences.conversionHistory.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('user.totalConversions')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Favorite sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {user.preferences.favoriteCurrencies.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('user.favoriteCurrencies')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Language sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {user.preferences.language.toUpperCase()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('user.preferredLanguage')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Current Settings Overview */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Language & Theme Settings */}
        <Box sx={{ flex: 1 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Language sx={{ mr: 1 }} />
              {t('user.languageAndTheme')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              <ListItem>
                <ListItemIcon>
                  <Language />
                </ListItemIcon>
                <ListItemText
                  primary={t('user.language')}
                  secondary={t(`language.${user.preferences.language}`)}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Palette />
                </ListItemIcon>
                <ListItemText
                  primary={t('user.theme')}
                  secondary={t(`theme.${user.preferences.theme}`)}
                />
              </ListItem>
            </List>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => setShowPreferences(true)}
              sx={{ mt: 2 }}
            >
              {t('user.editPreferences')}
            </Button>
          </Paper>
        </Box>

        {/* Currency Settings */}
        <Box sx={{ flex: 1 }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CurrencyExchange sx={{ mr: 1 }} />
              {t('user.currencySettings')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              <ListItem>
                <ListItemIcon>
                  <CurrencyExchange />
                </ListItemIcon>
                <ListItemText
                  primary={t('user.defaultFromCurrency')}
                  secondary={user.preferences.defaultFromCurrency}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CurrencyExchange />
                </ListItemIcon>
                <ListItemText
                  primary={t('user.defaultToCurrency')}
                  secondary={user.preferences.defaultToCurrency}
                />
              </ListItem>
            </List>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('user.favoriteCurrencies')}:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {user.preferences.favoriteCurrencies.map((currency) => (
                  <Chip key={currency} label={currency} size="small" />
                ))}
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Recent Conversions */}
        <Box sx={{ width: '100%' }}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <History sx={{ mr: 1 }} />
                {t('user.recentConversions')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {user.preferences.conversionHistory.length === 0 ? (
              <Alert severity="info">
                {t('user.noConversionsYet')}
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('user.from')}</TableCell>
                      <TableCell>{t('user.to')}</TableCell>
                      <TableCell align="right">{t('user.amount')}</TableCell>
                      <TableCell align="right">{t('user.result')}</TableCell>
                      <TableCell align="right">{t('user.rate')}</TableCell>
                      <TableCell align="right">{t('user.date')}</TableCell>
                      <TableCell align="center">{t('user.favorite')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {user.preferences.conversionHistory
                      .slice(-5)
                      .reverse()
                      .map((conversion) => (
                        <TableRow key={conversion.id}>
                          <TableCell>{conversion.fromCurrency}</TableCell>
                          <TableCell>{conversion.toCurrency}</TableCell>
                          <TableCell align="right">{conversion.amount}</TableCell>
                          <TableCell align="right">{conversion.convertedAmount}</TableCell>
                          <TableCell align="right">{conversion.rate}</TableCell>
                          <TableCell align="right">
                            {formatDate(conversion.timestamp)}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small">
                              {conversion.isFavorite ? (
                                <Star sx={{ color: 'warning.main' }} />
                              ) : (
                                <StarBorder />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Preferences Dialog */}
      <Dialog 
        open={showPreferences} 
        onClose={() => setShowPreferences(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('user.preferences')}</DialogTitle>
        <DialogContent>
          <UserPreferencesForm />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreferences(false)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Delete Account Confirmation */}
      <Dialog 
        open={showDeleteConfirm} 
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('user.deleteAccount')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('user.deleteAccountWarning')}
          </Alert>
          <Typography>
            {t('user.deleteAccountConfirmation')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained">
            {t('user.deleteAccount')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserProfilePage;
